/** Zero-profit boundaries. Reuses the existing rescue equation and fee model. */
import { Decimal } from 'decimal.js';
import { cashPayout } from './payout';
import { rescue } from './rescue';
import type { Row } from './row';

export function zeroOdds(raw: string): Decimal | null {
  try {
    const k = new Decimal(raw.trim().replace(',', '.'));
    if (!k.isFinite() || k.lte(1)) return null;
    return rescue('1', k, '2').breakEven;
  } catch { return null; }
}
export const zeroText = (n: Decimal, places = 2): string =>
  n.toDecimalPlaces(places, Decimal.ROUND_CEIL).toFixed(places).replace('.', ',');
export const ZERO_TABLE = Array.from({ length: 100 }, (_, i) => {
  const first = new Decimal(101 + i).div(100);
  return { first: first.toFixed(2), second: zeroOdds(first.toString())! };
});

export type ZeroBoundary = {
  source: string; value: Decimal; unit: 'odds' | 'cents'; current: Decimal;
  /** Comparison uses unrounded values, never the displayed threshold. */
  state: 'above' | 'edge' | 'below';
};
export type ZeroPair = { boundaries: ZeroBoundary[]; note: string; reason?: string };

/** Same monetary inputs as the rendered calculator, including its rounding.
 * Bookless mirrored shares have no verified fee schedule: never call that NET.
 * A boundary assumes the other stake remains fixed and this stake is rebalanced.
 */
export function zeroPair(row: Row): ZeroPair {
  const no = (reason: string): ZeroPair => ({ boundaries: [], note: '', reason });
  try {
    if (row.legs.length !== 2 || row.legs.some(l => l.lay || l.freebet || l.source === 'hand'))
      return no('порог — для двух обычных противоположных ставок');
    const [kind, , , , line] = (row.fork.market ?? '').split(':');
    if (!['WIN', 'TTL', 'HCP'].includes(kind ?? '') || row.legs.some(l => ['D', 'X'].includes(l.side)))
      return no('для этого рынка нужен отдельный расчёт');
    if (kind !== 'WIN' && (!line || !new Decimal(line).abs().mod(1).eq('0.5')))
      return no('возвраты и частичный расчёт: проверьте оба исхода');
    // Some mirrors omit unit/share as well as the order book. A prediction
    // venue rendered as ordinary odds is not evidence of a zero-fee payout.
    const prediction = new Set(['polymarket', 'poly', 'predictfun', 'kalshi', 'limitless', 'sxbet', 'azuro', 'myriad', 'cdna', 'smarkets', 'welstroy', 'opinion']);
    if (row.legs.some(l => l.unit !== 'share' && prediction.has(l.source)))
      return no('для рынка долей не подтверждены цена и комиссия');
    if (row.legs.some(l => !l.payoutModel))
      return no('для цены доли не подтверждены комиссия и объём');
    if (row.legs.some(l => !Number.isFinite(l.rub) || !Number.isFinite(l.payout) || l.rub <= 0 || l.payout <= l.rub))
      return no('по этим суммам нет порога безубыточного перекрытия');
    const net = row.legs.map(l => new Decimal(l.payout).div(l.rub));
    const out: ZeroBoundary[] = [];
    for (let i = 0; i < 2; i++) {
      const leg = row.legs[i]!;
      const model = leg.payoutModel!;
      const required = zeroOdds(net[1 - i]!.toString())!;
      let value: Decimal;
      let current: Decimal;
      if (model.kind === 'shares') {
        const rate = new Decimal(model.disabled ? '0' : model.rate);
        const exponent = model.exponent;
        if (!rate.isFinite() || rate.lt(0) || rate.gte(1) || ![1, 2].includes(exponent)) return no('неизвестная схема комиссии');
        // Solve net payout per dollar at a single price, using the same fee
        // function as paintLegs. Keep the safe lower-price side of the bracket.
        let lo = new Decimal('0.000000000001'), hi = new Decimal(1);
        for (let n = 0; n < 64; n++) {
          const p = lo.plus(hi).div(2);
          const effective = cashPayout(1, new Decimal(1).div(p), model, p);
          if (effective.gte(required)) lo = p; else hi = p;
        }
        value = lo.times(100);
        if (leg.tick && new Decimal(leg.tick).gt(0)) {
          const tick = new Decimal(leg.tick).times(100);
          value = value.div(tick).floor().times(tick);
          // A mathematically exact tick can sit at the upper bisection bound.
          const next = value.plus(tick), p = next.div(100);
          if (p.lt(1) && cashPayout(1, new Decimal(1).div(p), model, p).gte(required)) value = next;
        } else value = value.toDecimalPlaces(4, Decimal.ROUND_FLOOR);
        current = new Decimal(leg.cents ?? 0);
        if (value.lte(0) || current.lte(0)) return no('нет доступной цены для нуля');
      } else {
        const fee = new Decimal(model.rate);
        if (!fee.isFinite() || fee.lt(0) || fee.gte(1)) return no('неизвестная комиссия');
        value = new Decimal(1).plus(required.minus(1).div(new Decimal(1).minus(fee)));
        current = new Decimal(leg.odds);
        if (leg.unit === 'share') { value = new Decimal(100).div(value); current = new Decimal(leg.cents ?? 0); }
      }
      const delta = leg.unit === 'share' ? value.minus(current) : current.minus(value);
      out.push({ source: leg.source, value, current, unit: leg.unit === 'share' ? 'cents' : 'odds',
        state: delta.abs().lt('0.000000001') ? 'edge' : delta.gt(0) ? 'above' : 'below' });
    }
    const assumed = row.legs.some(l => l.payoutModel?.kind === 'shares' && l.payoutModel.assumed && !l.payoutModel.disabled);
    return { boundaries: out, note: assumed
      ? 'комиссия долей — 5% по настройке калькулятора; проверьте её на рынке'
      : 'по комиссиям калькулятора; суммы второго плеча нужно пересчитать' };
  } catch { return no('не хватает данных для порога'); }
}

export function boundaryText(b: ZeroBoundary): string {
  return b.unit === 'cents'
    ? `до ${b.value.toDecimalPlaces(4, Decimal.ROUND_FLOOR).toFixed(4).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',')} ¢`
    : `от ${zeroText(b.value, 3)}`;
}
